(ns io.daskul.webapp.widgets.segmented-filter-bar
  "The list view's segmented filters, as a horizontally scrollable bar (#622).

   Mantine's SegmentedControl is a single flex row that neither wraps nor
   scrolls. With six counted segments it overruns a 375px phone, and the list
   view's plain ScrollArea gave no sign there was more — stages past the third
   were unreachable unless you guessed you could drag. A ?segment= deep-link
   made it worse: the control could select a segment that was off screen, so
   the list filtered correctly while the control looked untouched.

   Two fixes, both edge-driven so a bar that fits is untouched:
   - an edge fade appears only on the side that has more content;
   - the active segment is scrolled into view when it changes.

   Geometry lives in the node-tested `.scroll` ns; this file is the DOM half."
  (:require
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.hooks.list-params :as list-params]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.segmented-filter-bar.scroll :as scroll]))

(def ^:private active-segment-selector
  "Mantine marks the selected SegmentedControl label with data-active."
  "[data-active]")

(defn- content-offset-left
  "A child's offset from the start of its scroll container's content, valid
   regardless of what the child's offsetParent happens to be."
  [^js container ^js el]
  (+ (- (.-left (.getBoundingClientRect el))
        (.-left (.getBoundingClientRect container)))
     (.-scrollLeft container)))

(defui segmented-filter-bar
  "One SegmentedControl per segmented filter, in a scroll container that
   signals its own overflow.

   Props:
     segmented-filters — the :segmented entries from :list :filters
     counts            — counts map for counts-field (nil until loaded)
     counts-field      — the field those counts belong to
     segment-keys      — {field segment-key} currently selected
     on-select         — (fn [filter-config field segment-key])"
  [{:keys [segmented-filters counts counts-field segment-keys on-select]}]
  (let [scroll-ref (uix/use-ref nil)
        [edges set-edges!] (uix/use-state {:left? false :right? false})

        measure!
        (uix/use-callback
         (fn []
           (when-let [^js el @scroll-ref]
             (set-edges! (scroll/scrollable-edges (.-scrollLeft el)
                                                  (.-scrollWidth el)
                                                  (.-clientWidth el)))))
         [])]

    ;; Keep the active segment on screen. Runs on mount too, which is what makes
    ;; a ?segment= deep-link to a late stage land somewhere legible.
    (uix/use-effect
     (fn []
       (when-let [^js el @scroll-ref]
         (when-let [^js active (.querySelector el active-segment-selector)]
           (let [target (scroll/scroll-left-for (.-scrollLeft el)
                                                (.-clientWidth el)
                                                (content-offset-left el active)
                                                (.-offsetWidth active))]
             ;; Assigning scrollLeft (rather than scrollIntoView) keeps this
             ;; strictly horizontal — scrollIntoView can also move the page.
             (when (not= target (.-scrollLeft el))
               (set! (.-scrollLeft el) target)))))
       (measure!)
       js/undefined)
     [segment-keys segmented-filters counts measure!])

    ;; Overflow depends on the viewport, so re-measure on resize/orientation.
    (uix/use-effect
     (fn []
       (.addEventListener js/window "resize" measure!)
       (fn [] (.removeEventListener js/window "resize" measure!)))
     [measure!])

    (let [mask (scroll/edge-mask edges)]
      ($ ui/box
        {:ref scroll-ref
         :onScroll measure!
         :mb "md"
         :data-testid "segmented-filter-bar"
         :style (cond-> {:overflowX "auto"
                         :overflowY "hidden"
                         ;; Hide the scrollbar itself — the fade is the
                         ;; affordance, and a native bar under a 44px control
                         ;; reads as chrome rather than a hint.
                         :scrollbarWidth "none"
                         :WebkitOverflowScrolling "touch"}
                  mask (assoc :maskImage mask :WebkitMaskImage mask))}
        ($ ui/stack {:gap "xs" :style {:width "fit-content"}}
          (for [f segmented-filters]
            (let [field (:field f)
                  field-counts (when (= field counts-field) counts)]
              ($ ui/segmented-control
                {:key (name field)
                 :value (get segment-keys field)
                 :onChange #(on-select f field %)
                 :data (clj->js (list-params/segment-data-with-counts (:segments f) field-counts))
                 :size "sm"
                 :style {:width "fit-content"}}))))))))
